import { useState } from 'react';

interface StarRatingProps {
  value: number;
  // Om provided: komponenten är interaktiv och anropar onChange vid klick.
  onChange?: (value: number) => void;
  size?: 'sm' | 'md' | 'lg';
  // För hover-preview när användaren glider över stjärnorna.
  showHover?: boolean;
  ariaLabel?: string;
}

// Fem-stjärnig rating. Klick på samma stjärna man redan har sätter värdet
// till 0 (toggle-bort). Hover-preview gör att man ser exakt vad man väljer
// innan man klickar.
export function StarRating({
  value,
  onChange,
  size = 'md',
  showHover = true,
  ariaLabel,
}: StarRatingProps) {
  const [hover, setHover] = useState<number | null>(null);
  const interactive = !!onChange;

  const px = size === 'lg' ? 'text-2xl' : size === 'sm' ? 'text-xs' : 'text-base';
  const minTouch = size === 'lg' ? 'min-w-[40px] min-h-[44px]' : 'min-w-[24px] min-h-[24px]';

  const display = hover !== null && showHover ? hover : value;

  return (
    <div
      className="flex items-center"
      role={interactive ? 'radiogroup' : undefined}
      aria-label={ariaLabel}
    >
      {[1, 2, 3, 4, 5].map((star) => {
        const filled = star <= display;
        return (
          <button
            key={star}
            type="button"
            disabled={!interactive}
            onClick={() => {
              if (!onChange) return;
              // Klick på samma stjärna man har → 0 (rensa)
              onChange(value === star ? 0 : star);
            }}
            onMouseEnter={() => interactive && setHover(star)}
            onMouseLeave={() => interactive && setHover(null)}
            className={`${px} ${minTouch} leading-none p-1 transition-transform ${
              filled ? 'text-gold' : 'text-loupe-text-3'
            } ${
              interactive
                ? 'cursor-pointer hover:scale-110'
                : 'cursor-default'
            } ${interactive ? '' : 'opacity-100'}`}
            aria-label={`${star} ${star === 1 ? 'star' : 'stars'}`}
            aria-checked={value === star}
            role={interactive ? 'radio' : 'img'}
          >
            ★
          </button>
        );
      })}
    </div>
  );
}
